import React from "react";
import { Button, Form, FormGroup, Spinner } from "react-bootstrap";
import { format, isValid, parseISO } from "date-fns";

import { updateOrganizationAppData } from "../../../../../../api/Organization";

export default function OrganizationAppDataForm(props: {
  organization: IOrganization;
}): React.JSX.Element {
  /**
   * A form to update the organization's app data.
   */
  const [organization, setOrganization] = React.useState<IOrganization>(
    props.organization,
  );

  const [appDataNotes, setAppDataNotes] = React.useState<string>(
    organization.appData?.notes || "",
  );
  const [appDataLastContacted, setAppDataLastContacted] =
    React.useState<Date | null>(organization.appData?.lastContacted || null);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [successMessage, setSuccessMessage] = React.useState("");
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const isFormComplete = (): boolean => {
    /**
     * Check that at least one field value has changed.
     */
    return (
      appDataNotes !== (organization.appData?.notes || "") ||
      appDataLastContacted !== (organization.appData?.lastContacted || null)
    );
  };

  const handleSubmitAppData = async (e: React.FormEvent) => {
    /**
     * Handle the form submission to update the BAG object's app data.
     */
    e.preventDefault();
    setIsLoading(true);
    // Clear any previous messages.
    setErrorMessage("");
    setSuccessMessage("");
    const organizationAppData: IOrganizationAppData = {
      notes: appDataNotes,
      lastContacted: appDataLastContacted,
    };
    await updateOrganizationAppData(organization.id, organizationAppData)
      .then(() => {
        setOrganization({ ...organization, appData: organizationAppData });
        setSuccessMessage("Aanpassingen zijn succesvol opgeslagen.");
      })
      .catch((error: IApiError) => setErrorMessage(error.message));
    setIsLoading(false);
  };

  return (
    <Form
      className="d-flex flex-column flex-grow-1 justify-content-between align-items-center mt-3"
      onSubmit={handleSubmitAppData}
    >
      <div className="w-100">
        <FormGroup className="mb-3">
          <Form.Label htmlFor="notes">
            <span className="mb-0">
              <strong>Notities</strong>
            </span>
          </Form.Label>
          <Form.Control
            size="sm"
            className="rounded-0"
            type="text"
            as="textarea"
            rows={2}
            value={appDataNotes}
            onChange={(e) => {
              setAppDataNotes(e.target.value);
              setSuccessMessage("");
            }}
            required={false}
            id="notes"
          />
        </FormGroup>

        <FormGroup className="mb-3">
          <Form.Label htmlFor="last_contacted">
            <span className="mb-0">
              <strong>Meest recente contactmoment</strong>
            </span>
          </Form.Label>
          <Form.Control
            size="sm"
            type="date"
            value={
              appDataLastContacted && isValid(appDataLastContacted)
                ? format(appDataLastContacted, "yyyy-MM-dd")
                : ""
            }
            onChange={(e) => {
              setAppDataLastContacted(
                e.target.value ? parseISO(e.target.value) : null,
              );
              setSuccessMessage("");
            }}
            required={false}
            id="last_contacted"
          />
          <Form.Text muted={true}>
            De datum waarop er voor het laatst contact is opgenomen met het
            bedrijf.
          </Form.Text>
        </FormGroup>

        <div className="d-flex w-100 align-items-center justify-content-between">
          {errorMessage ? (
            <Form.Text className="text-danger mb-1">{errorMessage}</Form.Text>
          ) : (
            <div>
              {successMessage && (
                <Form.Text className="text-success mb-1">
                  {successMessage}
                </Form.Text>
              )}
            </div>
          )}
          <Button
            size="sm"
            title="Opslaan"
            type="submit"
            variant="primary"
            className="px-4"
            disabled={isLoading || !isFormComplete()}
            style={{ minWidth: "7rem" }}
          >
            {isLoading ? (
              <Spinner animation="border" variant="dark" size="sm" />
            ) : (
              "Opslaan"
            )}
          </Button>
        </div>
      </div>
    </Form>
  );
}
